import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Upload, Search, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function ImageAnalysisPage() {
  const { user } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [prompt, setPrompt] = useState('Describe this image in detail');
  const [analysis, setAnalysis] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef(null);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result.split(',')[1]);
      setImagePreview(reader.result);
      setAnalysis('');
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!image) { toast.error('Please select an image first'); return; }
    setAnalyzing(true);
    setAnalysis('');
    try {
      const res = await api.post('/analyze-image', { image, prompt });
      setAnalysis(res.data.analysis);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    }
    finally { setAnalyzing(false); }
  };

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="image-analysis-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">Image Analysis</h1>
      <p className="text-sm text-muted-foreground mb-8">Upload an image and let AI analyze it for you</p>

      {/* Upload Area */}
      <div
        data-testid="analysis-upload-area"
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-border/60 hover:border-gold/50 transition-colors cursor-pointer p-8 text-center mb-6"
      >
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleSelect} data-testid="analysis-file-input" />
        {imagePreview ? (
          <img src={imagePreview} alt="Selected" className="max-h-64 mx-auto object-contain" />
        ) : (
          <>
            <ImageIcon size={32} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium">Click to select an image</p>
            <p className="text-xs text-muted-foreground">JPEG, PNG, WebP supported</p>
          </>
        )}
      </div>

      {/* Prompt */}
      <div className="mb-6">
        <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Analysis Prompt</label>
        <Textarea
          data-testid="analysis-prompt"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none text-sm"
          rows={2}
        />
      </div>

      <Button
        data-testid="analyze-btn"
        onClick={handleAnalyze}
        disabled={!image || analyzing}
        className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300 mb-8"
      >
        {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing...</> : <><Search size={14} className="mr-2" /> Analyze Image</>}
      </Button>

      {/* Results */}
      {analysis && (
        <div className="border border-border/40 p-6 animate-in" data-testid="analysis-result">
          <h2 className="font-serif text-lg mb-4">Analysis Result</h2>
          <div className="text-sm font-sans leading-relaxed whitespace-pre-wrap text-foreground/80">
            {analysis}
          </div>
        </div>
      )}
    </div>
  );
}
